/**
 * Academy domain — presentational rows for the shared live panel (Q&A · Chat ·
 * Reactions · Participants), consumed by the producer control room and the viewer
 * watch page through `AcademyLivePanelStore`.
 *
 * These row shapes deliberately decouple the panel from the two raw wire formats it
 * merges: the camelCase SignalR hub payloads (`QuestionAddedEvent` /
 * `ChatMessageAddedEvent` / `ReactionCounts`) and the PascalCase REST view-models
 * (`LiveQuestionResponse` / `ChatMessageResponse` / `AttendanceItem`). The
 * store does the merge + name resolution once; the panel only renders rows.
 * All row properties are PascalCase — the store is the single adaptation point.
 */
import type { Guid, LiveQuestionStatusType, ReactionType } from './academy-enums.model';
import type { MutedParticipantItem } from './academy-responses.model';

/** A question row, decoupled from the camelCase hub payload / PascalCase REST shapes. */
export interface LiveQuestionRow {
  QuestionId: Guid;
  /** Author id (for moderation calls); null when only the REST row is known. */
  PersonId: Guid | null;
  /** Display name, or null when the server has none for this person. */
  Name: string | null;
  /** The asker's account name — producer studio only, and null while the server has none. */
  TenantName: string | null;
  Content: string;
  UpvoteCount: number;
  Status: LiveQuestionStatusType;
  /** Engagement mode: asked by the session user (`PersonId` is theirs). */
  Mine: boolean;
  /** Engagement mode: already upvoted (one-way — the REST upvote is idempotent). */
  Voted: boolean;
}

/** A chat message row. */
export interface LiveChatRow {
  ChatMessageId: Guid;
  PersonId: Guid | null;
  Name: string | null;
  /** The writer's account name — producer studio only, and null while the server has none. */
  TenantName: string | null;
  Text: string;
  /** The writer passed the live's producer gate when the message was sent. */
  IsProducer: boolean;
  /** Moderation mode: the author is currently muted. */
  Muted: boolean;
}

/**
 * A mute/unmute click of the producer studio that the server has not confirmed yet. It stands
 * only while nothing fresher came from the server about that person: a hub change that arrived
 * after the click, or a muted-list read that landed after it, is the truth and the click's
 * mark is ignored.
 */
export interface PendingMute {
  isMuted: boolean;
  /** `AcademyRealtimeStore.muteChangeCount` when the click was made. */
  afterChange: number;
  /** The muted list the studio held when the click was made (a new array per read). */
  overRead: readonly MutedParticipantItem[] | undefined;
}

/** An aggregated reaction count, mapped to its emoji. */
export interface LiveReactionRow {
  Type: ReactionType;
  Emoji: string;
  Count: number;
}

/**
 * A participant roster row (moderation mode — built from `GET /LiveEvents/{id}/attendances`
 * plus the hub's `ParticipantJoined/Left`). Live sessions only: the endpoint excludes VOD.
 */
export interface LiveParticipantRow {
  PersonId: Guid;
  /** Display name, or null when the person service has none for this person. */
  Name: string | null;
  /** The participant's account name, or null while the server has none. */
  TenantName: string | null;
  /** Session state: watching live now, or already left. */
  Status: 'live' | 'left';
  JoinedOnUtc: string;
  /** Pre-formatted `mm:ss` watched time. */
  WatchedLabel: string;
}

/** Aggregated reaction types → emoji (real mapping, not mock). */
export const REACTION_EMOJI: Record<ReactionType, string> = {
  Like: '👍',
  Love: '❤️',
  Clap: '👏',
  Wow: '😮',
  Laugh: '😂',
};

/** Stable design order for the reaction send-buttons / tallies. */
export const REACTION_ORDER: ReactionType[] = ['Like', 'Love', 'Clap', 'Wow', 'Laugh'];

/** Which face the producer studio's right column wears, derived from the broadcast status. */
export type StudioRightMode = 'setup' | 'interaction' | 'recap';

/**
 * Signal-health seam of the producer studio (`GET /LiveEvents/{id}/ingest/health`,
 * backend-pending — doc §8/§14). Until that endpoint exists the studio holds `null`:
 * `connected` falls back to "Live ⇒ connected" and Resolução/FPS render "—". Feeding it
 * later lights the grace ("Sinal perdido") state and the real numbers with no other change.
 */
export interface StudioSignalHealth {
  connected: boolean;
  resolution: string | null;
  fps: number | null;
}
